<script>
  import { onMount, onDestroy } from "svelte";
  import { progress } from "../lib/state.svelte.js";
  import { getAuthState, getLeaderboard, installBridge } from "../lib/online.js";
  import { eraForEvo } from "../../data/journeyEngine.js";
  import { ERA_BY_ID } from "../../data/eras.js";
  import { fmtEra, fmtFuture, fmtEvo, fmtSince, toEpochMs } from "../lib/format.js";
  import { organismInner, defaultOrganism } from "../../data/organism.js";
  import { go } from "../lib/nav.svelte.js";
  import CampaignBanner from "./CampaignBanner.svelte";
  import ProfileModal from "./ProfileModal.svelte";

  let tick = $state(0);
  let unsub = null;
  let selected = $state(null); // { profile, rank, isMe } | null

  function openProfile(profile, rank, isMe) {
    selected = { profile, rank, isMe };
  }
  function closeProfile() {
    selected = null;
  }

  onMount(() => {
    unsub = installBridge(() => tick++);
  });
  onDestroy(() => unsub && unsub());

  const auth = $derived.by(() => {
    tick;
    return getAuthState();
  });
  const rows = $derived.by(() => {
    tick;
    return getLeaderboard();
  });
  const me = $derived(eraForEvo(progress.evo));
  const meProfile = $derived({
    nickname: progress.nickname,
    bio: progress.bio,
    evo: progress.evo,
    eraId: me.era.id,
    form: me.era.form,
    organism: progress.organism,
    solvedCount: Object.keys(progress.solved).length,
  });

  function eraLabel(row) {
    const era = ERA_BY_ID[row.eraId];
    if (!era) return "";
    const at = eraForEvo(row.evo || 0);
    const when = at.isFuture ? fmtFuture(at.futureMa) : fmtEra(at.ma);
    return `${era.em} ${era.name} · ${when}`;
  }

  /** 最終ログイン。まだ記録のない古い参加者は最終同期日時で代用する。 */
  function lastSeen(row) {
    const t = toEpochMs(row.lastLoginAt) || toEpochMs(row.updatedAt);
    return t ? fmtSince(t) : "";
  }
</script>

<section>
  <div class="card">
    <h2 style="margin:0">🏆 到達した時代のランキング</h2>
  </div>

  <div class="card">
    {#if !auth.syncEnabled}
      <div class="empty-state">
        <span class="em">🔌</span>
        <p><b>オンライン機能は設定されていません。</b></p>
        <p class="tiny">
          リポジトリ直下の <code>firebase-config.js</code> に自分の Firebase
          プロジェクトの設定値を入れ、Web に公開するとランキングが使えます（手順は README）。<br />
          設定しなくても、学習・旅・統計はすべてこのまま使えます。
        </p>
      </div>
    {:else if rows === null}
      <div class="empty-state">
        <span class="em">⏳</span>
        読み込み中…
      </div>
    {:else if rows.length === 0}
      <div class="empty-state">
        <span class="em">🌱</span>
        まだ誰も登録していません。最初の一人になりましょう。
      </div>
    {:else}
      {#each rows as row, i (row.uid || i)}
        {@const mine = auth.user && row.uid === auth.user.uid}
        <div
          class="lb-row clickable"
          class:me={mine}
          role="button"
          tabindex="0"
          onclick={() => openProfile(row, i + 1, mine)}
          onkeydown={(e) => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), openProfile(row, i + 1, mine))}
          title="プロフィールを見る"
        >
          <span class="lb-rank">{i + 1}</span>
          <span class="lb-av">
            <svg viewBox="0 0 100 100" aria-hidden="true">
              {@html organismInner(row.form || "protocell", row.organism || defaultOrganism())}
            </svg>
          </span>
          <span class="lb-main">
            <div class="lb-name">{row.nickname || "ななしの生物"}</div>
            <div class="lb-sub">{eraLabel(row)} · {row.solvedCount || 0}問</div>
            {#if lastSeen(row)}
              <div class="lb-seen">🕒 最終ログイン {lastSeen(row)}</div>
            {/if}
          </span>
          <span class="evo-tag">{fmtEvo(row.evo || 0)}</span>
        </div>
      {/each}
    {/if}
  </div>

  <div class="card">
    <h3>あなたの記録</h3>
    {#if auth.syncEnabled && !auth.user}
      <CampaignBanner />
      <div class="btn-row" style="margin-bottom:12px">
        <button class="btn submit join" onclick={() => go("account")}>
          🚀 登録してランキングに参加しよう！
        </button>
      </div>
    {/if}
    <div
      class="lb-row me clickable"
      role="button"
      tabindex="0"
      onclick={() => openProfile(meProfile, null, true)}
      onkeydown={(e) => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), openProfile(meProfile, null, true))}
      title="プロフィールを見る"
    >
      <span class="lb-rank">—</span>
      <span class="lb-av">
        <svg viewBox="0 0 100 100" aria-hidden="true">
          {@html organismInner(me.era.form, progress.organism)}
        </svg>
      </span>
      <span class="lb-main">
        <div class="lb-name">{progress.nickname || "ななしの生物"}</div>
        <div class="lb-sub">
          {me.era.em}
          {me.era.name} · {me.isFuture ? fmtFuture(me.futureMa) : fmtEra(me.ma)} ·
          {Object.keys(progress.solved).length}問
        </div>
      </span>
      <span class="evo-tag">{fmtEvo(progress.evo)}</span>
    </div>
    <p class="tiny muted" style="margin:8px 2px 0">
      💡 プロフィールは「⚙️ アカウント・設定」から書けます。ランキングの誰かをタップすると、その人の自己紹介が見られます。
    </p>
  </div>
</section>

{#if selected}
  <ProfileModal
    profile={selected.profile}
    rank={selected.rank}
    isMe={selected.isMe}
    onClose={closeProfile}
  />
{/if}
